import ArrowDownIcon from "@assets/icons/arrow-down.svg";
import ArrowUpIcon from "@assets/icons/arrow-up.svg";
import CareIcon from "@assets/icons/care.svg";
import { useI18n } from "@core/i18n/i18n";
import { useFormatMedicalCondition } from "@domain/medical-condition/use-medical-condition";
import { useFormatGender } from "@domain/patient/hooks/use-gender";
import { useGenderIcon } from "@domain/patient/hooks/use-gender-icon";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";
import { isPendingPatient, Patient, PendingPatient } from "@domain/patient/patient";
import styled from "@emotion/styled";
import { ContentWithTooltip } from "@ui/components/common/content-with-tooltip";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { useCountryConfig } from "@ui/pages/patient-details/hooks/use-country-config";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotEmpty, isNotNil } from "@utils/assert";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

interface PatientDetailsColumnProps {
	patient: Patient | PendingPatient;
	isLightMode?: boolean;
	isEditable?: boolean;
	setContainerHeight?: (height: number) => void;
}

const ACCORDION_VERTICAL_PADDING = 2 * 16;

export const PatientDetailsColumn: React.FC<PatientDetailsColumnProps> = ({ patient }) => {
	const { isDesktop } = useDimensions();
	const [containerHeight, setContainerHeight] = useState<number>(0);

	return (
		<>
			{isDesktop ? (
				<StickyPatientDetailsContainer>
					<StickyPatientDetailsSection>
						<PatientDetailsContent patient={patient} />
					</StickyPatientDetailsSection>
				</StickyPatientDetailsContainer>
			) : (
				<BackgroundContainer>
					<AccordionPatientDetailsContainer height={containerHeight}>
						<AccordionPatientDetailsSection>
							<PatientDetailsContent patient={patient} setContainerHeight={setContainerHeight} />
						</AccordionPatientDetailsSection>
					</AccordionPatientDetailsContainer>
				</BackgroundContainer>
			)}
		</>
	);
};

export const PatientDetailsContent: React.FC<PatientDetailsColumnProps> = ({
	patient,
	isLightMode = false,
	isEditable = true,
	setContainerHeight,
}) => {
	const { format, formatAge, formatDateMed } = useI18n();
	const { formatPatientName } = useFormatPatients();
	const formatGender = useFormatGender();
	const { getGenderIcon } = useGenderIcon();
	const { formatDisease, formatFunctionalStatus } = useFormatMedicalCondition();
	const dialogContext = useContext(DialogContext);
	const { isDesktop } = useDimensions();
	const { shouldDisplayLegalAgeDisclaimer } = useCountryConfig();

	const [closedContainerHeight, setClosedContainerHeight] = useState<number>(0);
	const [openedContainerHeight, setOpenedContainerHeight] = useState<number>();

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const headerInfoRef = useRef<HTMLDivElement>(null);
	const patientDetailsRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!patientDetailsRef.current?.clientHeight || !headerInfoRef.current?.clientHeight) {
			return;
		}
		setOpenedContainerHeight(patientDetailsRef.current.clientHeight);
		setClosedContainerHeight(headerInfoRef.current.clientHeight);
		setContainerHeight?.(headerInfoRef.current.clientHeight + ACCORDION_VERTICAL_PADDING);

		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [patientDetailsRef.current, headerInfoRef.current]);

	const patientAgeGender = useMemo(
		() =>
			!isPendingPatient(patient)
				? [
						...(patient.birthDate ? [formatAge(patient.birthDate)] : []),
						...(patient.gender ? [formatGender(patient.gender)] : []),
					].join(", ")
				: undefined,
		[formatAge, formatGender, patient],
	);

	const GenderIcon = useMemo(() => getGenderIcon(patient), [getGenderIcon, patient]);

	const toggleAccordion = (newStatus: boolean) => {
		if (newStatus && openedContainerHeight) {
			setIsOpen(true);
			setContainerHeight?.(openedContainerHeight + ACCORDION_VERTICAL_PADDING);
		}
		if (!newStatus && closedContainerHeight) {
			setIsOpen(false);
			setContainerHeight?.(closedContainerHeight + ACCORDION_VERTICAL_PADDING);
		}
	};

	const lightModeStyle = { color: isLightMode ? colors.grey : colors.white };

	return (
		<DetailsContainer ref={patientDetailsRef}>
			<HeaderContainer>
				<HeaderInfo ref={headerInfoRef}>
					<GenderIcon width="40" height="40" />
					<HeaderInfoTextContainer>
						<PatientName className="p-size-m-bold" style={lightModeStyle}>
							<abbr style={{ textDecoration: "none" }} title={formatPatientName(patient)}>
								{formatPatientName(patient)}
							</abbr>
						</PatientName>
						{patientAgeGender && <div style={lightModeStyle}>{patientAgeGender}</div>}
					</HeaderInfoTextContainer>
				</HeaderInfo>
				{!isDesktop && (
					<SwitchButton onClick={() => toggleAccordion(!isOpen)}>
						{isOpen ? (
							<ArrowUpIcon height={24} width={24} color={colors.white} />
						) : (
							<ArrowDownIcon height={24} width={24} color={colors.white} />
						)}
					</SwitchButton>
				)}
			</HeaderContainer>

			{isNotNil(patient.email) && (
				<>
					<PatientDetailsGroup>
						<PatientDetailsTitle text={format("patientDetails.column.contactSection")} isLightMode={isLightMode} />

						<EmailText style={lightModeStyle}>
							<abbr style={{ textDecoration: "none" }} title={patient.email}>
								{patient.email}
							</abbr>
						</EmailText>
					</PatientDetailsGroup>
					<Separator isLightMode />
				</>
			)}

			{isPendingPatient(patient) && (
				<>
					<PatientDetailsGroup>
						<PatientDetailsTitle
							text={format("patientDetails.column.pendingPatient.invitationCode")}
							isLightMode={isLightMode}
						/>
						<div style={lightModeStyle}>{patient.activationCode}</div>
					</PatientDetailsGroup>
					<Separator isLightMode />
				</>
			)}

			{!isPendingPatient(patient) && (
				<PatientDetailsGroup>
					<ConditionTitle>
						<PatientDetailsTitle text={format("patientDetails.column.medicalSection")} isLightMode={isLightMode} />
						{isEditable && (
							<PressableIcon
								className="pi pi-pencil"
								onClick={() =>
									dialogContext?.openModal(ModalId.EditPatientInfo, {
										patient,
									})
								}
								isLightMode={isLightMode}
							/>
						)}
					</ConditionTitle>
					<div style={lightModeStyle}>{formatDisease(patient.disease)}</div>
				</PatientDetailsGroup>
			)}

			<PatientDetailsGroup>
				{isPendingPatient(patient) ? (
					<ConditionTitle>
						<PatientDetailsTitle
							text={format("patientDetails.column.functionalStatusSection")}
							isLightMode={isLightMode}
						/>
						{isEditable && (
							<PressableIcon
								className="pi pi-pencil"
								onClick={() => {
									dialogContext?.openModal(ModalId.EditPatientInfo, {
										patient,
									});
								}}
								isLightMode={isLightMode}
							/>
						)}
					</ConditionTitle>
				) : (
					<PatientDetailsTitle
						text={format("patientDetails.column.functionalStatusSection")}
						isLightMode={isLightMode}
					/>
				)}
				<div style={lightModeStyle}>{formatFunctionalStatus(patient.functionalStatus)}</div>
			</PatientDetailsGroup>

			<Separator isLightMode />

			{!isPendingPatient(patient) && isNotEmpty(patient.aspiration) && (
				<>
					<PatientDetailsGroup>
						<PatientAspirationTitle>
							<ContentWithTooltip
								tooltip={format("patientDetails.column.patientAspiration.tooltip")}
								iconColor={isLightMode ? colors.grey : colors.white}
							>
								<PatientDetailsTitle
									isLightMode={isLightMode}
									text={format("patientDetails.column.patientAspiration")}
								/>
							</ContentWithTooltip>
						</PatientAspirationTitle>
						<PatientAspiration style={lightModeStyle}>{patient.aspiration}</PatientAspiration>
					</PatientDetailsGroup>
					<Separator isLightMode />
				</>
			)}

			<div>
				<PatientDetailsTitle text={format("patientDetails.column.profileCreation")} isLightMode={isLightMode} />
				<div style={lightModeStyle}>{formatDateMed(patient.creationDate)}</div>
			</div>

			{shouldDisplayLegalAgeDisclaimer(patient) && (
				<LegalAgeDisclaimerContainer>
					<Row>
						<CareIcon width={16} height={16} />
						<div className="p-size-s-bold" color={colors.white}>
							{format("patientDetails.column.legalAgeDisclaimer.title")}
						</div>
					</Row>
					<div className="p-size-xs-semi-bold" color={colors.white}>
						{format("patientDetails.column.legalAgeDisclaimer.description")}
					</div>
				</LegalAgeDisclaimerContainer>
			)}
		</DetailsContainer>
	);
};

const PatientDetailsTitle = ({ text, isLightMode }: { text: string; isLightMode: boolean }) => (
	<div className="p-size-s-bold" style={{ color: isLightMode ? colors.grey : colors.white }}>
		{text}
	</div>
);

const StickyPatientDetailsContainer = styled.section`
	min-width: 228px;
	max-width: 20vw;
	background-color: ${colors.grey};
	border-radius: 16px 16px 0 0;
	color: ${colors.white};

	abbr {
		text-decoration: inherit;
	}
`;

const AccordionPatientDetailsSection = styled.section`
	background-color: ${colors.grey};
	color: ${colors.white};
	padding: 16px;

	abbr {
		text-decoration: inherit;
	}
`;

const StickyPatientDetailsSection = styled.div`
	position: sticky;
	top: 0;
	padding: 24px;
`;

const DetailsContainer = styled.div`
	${stack(16)};
`;

const BackgroundContainer = styled.div`
	background-color: ${colors.primaryBackground};
	padding-bottom: 24px;
`;

const AccordionPatientDetailsContainer = styled.div<{ height: number }>`
	height: ${({ height }) => (height ? `${height}px` : "unset")};
	transition: height 0.5s ease-in-out;
	overflow: hidden;
	margin: 0 16px;
	border-radius: 16px;
`;

const PressableIcon = styled.i<{ isLightMode: boolean }>`
	cursor: pointer;
	font-size: 13px;
	color: ${({ isLightMode }) => (isLightMode ? colors.grey : colors.white)};
`;

const ConditionTitle = styled.div`
	${row("S", "spacebetween", "center")}
`;

const PatientAspiration = styled.div`
	overflow-wrap: break-word;
`;

const PatientAspirationTitle = styled.div`
	display: flex;
	gap: 4px;
	place-items: center;
`;

const PatientDetailsGroup = styled.div`
	width: 100%;
`;

const Separator = styled.div<{ isLightMode: boolean }>`
	height: 1px;
	width: 100%;
	background-color: ${({ isLightMode }) => (isLightMode ? colors.primary10 : colors.grey80)};
`;

const HeaderContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-self: stretch;
	justify-content: space-between;
`;

const HeaderInfo = styled.div`
	display: grid;
	grid-template-columns: 40px 1fr;
	place-items: center;
	gap: 16px;
`;

const HeaderInfoTextContainer = styled.div`
	display: grid;
	place-content: center;
`;

const SwitchButton = styled.button`
	cursor: pointer;
	border: 0;
	border-radius: 8px;
	padding: 16px 24px;
	background-color: ${colors.transparent};
	${row(undefined, "center", "center")};
	margin-right: -8px;
`;

const EmailText = styled.div`
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
`;

const PatientName = styled.div`
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
	overflow-wrap: break-word;
`;
const LegalAgeDisclaimerContainer = styled.div`
	margin-top: 8px;
	padding: 16px;
	border-radius: 8px;
	${stack(0, "spacebetween", "flexstart")}
	background-color: ${colors.grey80};
`;
const Row = styled.div`
	${row(8, "flexstart", "center")}
`;
